import _ from "lodash";
import { axiosInstance } from "./globals";
import * as userService from "./userService";

import { DataSourceRequest } from "@/model/DataSourceRequest";
import { DataSourceResult } from "@/model/DataSourceResult";
import { JoinWithNameParam } from "@/model/JoinWithNameParam";
import { MeetingFilter } from "@/model/filters";

export async function createMeeting(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Meeting/AddNewMeeting", data, { headers })).data;
}

export async function getAllMeetings(filter: MeetingFilter): Promise<DataSourceResult<any>> {
  const headers = userService.getAuthHeaders();
  const params = {
    fromDate: filter.fromDate,
    toDate: filter.toDate,
  };
  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: [],
    sorts: [{ field: "CreatedAt", dir: "desc" }],
  };

  if (filter.status) {
    body.filters.push({
      field: "status",
      operator: "eq",
      value: filter.status,
      logic: "and",
    });
  }
  if (filter.q) {
    const isNumber = !isNaN(Number(filter.q));
    if (filter.status != null) {
      if (isNumber) {
        body.filters[0].filters = [
          { field: "id", operator: "eq", value: Number(filter.q), logic: "or" },
          { field: "name", operator: "contains", value: filter.q, logic: "or" },
          { field: "apiConfig.apiUrl", operator: "contains", value: filter.q, logic: "or" }
        ];
      } else {
        body.filters[0].filters = [
          { field: "name", operator: "contains", value: filter.q, logic: "or" },
          { field: "apiConfig.apiUrl", operator: "contains", value: filter.q, logic: "or" }
        ];

      }
    } else {
      if (isNumber) {
        body.filters.push(
          { field: "id", operator: "eq", value: Number(filter.q), logic: "or" },
          { field: "name", operator: "contains", value: filter.q, logic: "or" },
          { field: "apiConfig.apiUrl", operator: "contains", value: filter.q, logic: "or" }
        );
      } else {
        body.filters.push(
          { field: "name", operator: "contains", value: filter.q, logic: "or" },
          { field: "apiConfig.apiUrl", operator: "contains", value: filter.q, logic: "or" });
      }
    }
  }

  /// isRecorded
  if (filter.isRecorded != null) {
    body.filters.push(
      { field: "customerId", operator: "eq", value: filter.customerId, logic: "and" }
    );
    body.filters.push(
      { field: "isRecorded", operator: "eq", value: filter.isRecorded, logic: "and" },
    );
    body.filters.push(
      { field: "recordingSize", operator: "gt", value: 0, logic: "and" },
    );
  }
  return (await axiosInstance.post<DataSourceResult<any>>("/Meeting/GetMeetingList", body, { headers, params })).data;
}

export async function getAllMeetingsByCustomer(
  customerId: number | string,
  filter: MeetingFilter
): Promise<DataSourceResult<any>> {
  const headers = userService.getAuthHeaders();

  const body: DataSourceRequest = {
    page: filter.page,
    pageSize: filter.pageSize,
    filters: [],
    sorts: [{ field: "id", dir: "desc" }],
  };

  if (filter.q) {
    body.filters.push({
      field: "name",
      operator: "contains",
      value: filter.q,
      logic: "and",
    });
  }

  if (filter.status) {
    body.filters.push({
      field: "status",
      operator: "eq",
      value: filter.status,
      logic: "and",
    });
  }

  return (
    await axiosInstance.post<DataSourceResult<any>>(`/Meeting/GetMeetingListByCustomer/${customerId}`, body, {
      headers,
    })
  ).data;
}

export async function getDetails(id: number) {
  const headers = userService.getAuthHeaders();
  const meeting = (await axiosInstance.get(`/Meeting/GetMeetingDetail/${id}`, { headers })).data;

  if (meeting.status === "INPROGRESS") {
    meeting.participantsJoined = (await axiosInstance.get(`/Meeting/ParticipantsJoined/${id}`, { headers })).data;

    for (const p of meeting.participantsJoined) {
      const matchParticipantByName = meeting.meetingParticipants.find((it) => it.name === p.name);
      if (matchParticipantByName) {
        p.email = matchParticipantByName.email;
        p.phone = matchParticipantByName.phone;
      }
    }

    meeting.participantsNotJoined = meeting.meetingParticipants.filter((m) =>
      meeting.participantsJoined.every((pj) => pj.name !== m.name)
    );
  }

  return meeting;
}

export async function getStoragePlan() {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/Meeting/StoragePlan", { headers })).data;
}

export async function updateMeeting(data: any) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Meeting/UpdateMeeting", data, { headers })).data;
}

export async function cancelMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/CancelMeeting", null, { headers, params })).data;
}

export async function deleteMeeting(id: number) {
  const headers = userService.getAuthHeaders();
  const params = { id };
  return (await axiosInstance.post("/Meeting/DeleteMeeting", null, { headers, params })).data;
}

export async function startMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/StartMeeting", null, { headers, params })).data;
}

export async function joinMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/JoinMeeting", null, { headers, params })).data;
}

export async function endMeeting(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };
  return (await axiosInstance.post("/Meeting/EndMeeting", null, { headers, params })).data;
}

export async function startNewMeeting() {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/Meeting/StartNewMeeting", {}, { headers })).data;
}

export async function sendInvitationEmails(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };

  return (await axiosInstance.post("/Meeting/SendInvitation", null, { headers, params })).data;
}

export async function sendNotificationEmails(meetingId: number, status: string) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, status };

  return (await axiosInstance.post("/Meeting/SendEmailNotification", null, { headers, params })).data;
}

export async function sendSingleInvitationEmail(meetingId: number, participantId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, participantId };

  return (await axiosInstance.post("/Meeting/SendSingleInvitation", null, { headers, params })).data;
}

export async function sendInvitationSms(meetingId: number, participantId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, participantId };

  return (await axiosInstance.post("/Meeting/SendSmsSingleInvitation", null, { headers, params })).data;
}

export async function addNewParticipant(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/MeetingParticipants/AddNewParticipant", data, { headers })).data;
}

export async function updateParticipant(data) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.post("/MeetingParticipants/UpdateParticipant", data, { headers })).data;
}

/**
 * Lấy thông tin accessAdmin từ api GetMeetingDetail.
 * Nếu accessCode là loại accessAdmin thì backend xác định vai trò (OPERATOR) và trạng thái có thể mở lại cuộc họp (isReOpen)
 */
export async function getBySecretKey(meetingSecretKey: string, accessCode?: string) {
  const params: any = { meetingSecretKey };
  if (accessCode) {
    params.accessCode = accessCode;
  }

  return (await axiosInstance.get("/JoinMeetings/GetMeetingInfo", { params })).data;
}

export async function joinBySecretKey(params: { meetingSecretKey: string; accessCode: string; org?: string }) {
  return (await axiosInstance.post("/JoinMeetings/Join", null, { params })).data;
}

export async function joinBySecretKeyAndName(params: {
  meetingSecretKey: string;
  accessCode: string;
  fullName: string;
  org?: string;
}) {
  return (await axiosInstance.post("/JoinMeetings/JoinWithName", null, { params })).data;
}

export async function joinWithName(params: JoinWithNameParam) {
  return (await axiosInstance.post("/JoinMeetings/JoinWithName", null, { params })).data;
}

export async function getMeetingRecording(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };

  return (await axiosInstance.post("/Meeting/MeetingRecording", null, { headers, params })).data;
}

export async function getMeetingRecordingLinks(meetingId: number) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId };

  return (await axiosInstance.get("/Meeting/MeetingRecordingLinks", { headers, params })).data;
}


export async function deleteRecording(customerId: number, internalMeetingId: string) {
  const headers = userService.getAuthHeaders();
  const data = { customerId, internalMeetingId };

  return (await axiosInstance.post("/Customer/DeleteRecording", data, { headers })).data;
}

//Meeting/DeleteRecording?meetingId=84056&type=mp4
export async function deleteRecordingByType(meetingId: number, type: string) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, type };
  return (await axiosInstance.post("/Meeting/DeleteRecording", null, { headers, params })).data;
}

export async function getPreviewMeetingEmail(meetingId: number, status: string) {
  const headers = userService.getAuthHeaders();
  const params = { meetingId, status };

  return (await axiosInstance.get("/Meeting/PreviewMeetingEmail", { headers, params })).data;
}

export async function getParticipantsMeeting(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/ThongKe/GetParticipantMeeting", { headers, params })).data.data;
}

export async function getLogParticipantsMeeting(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/ThongKe/GetLogParticipantsMeeting", { headers, params })).data.data;
}
export async function getSummaryMeeting(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/Meeting/GetSummaryMeeting", { headers, params })).data.data;
}

// tóm tắt theo timeline
export async function getSummaryTimeLine(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/Meeting/GetSummaryByTime", { headers, params })).data.data;
}
// tóm tắt theo người phát biểu
export async function getSummaryAllUser(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/Meeting/GetSummaryAllUser", { headers, params })).data.data;
}
export async function getContentMeeting(params) {
  const headers = userService.getAuthHeaders();
  return (await axiosInstance.get("/ThongKe/GetContentMeeting", { headers, params })).data.data;
}
export async function updateApiConfig(meetingId: number, apiConfigId: number) {
  const headers = userService.getAuthHeaders();
  const data = { meetingId, apiConfigId };
  return (await axiosInstance.post("/Meeting/UpdateApiConfig", data, { headers })).data;
}

// Hàm edit số lượng người max tham gia
export async function editMaxParticipants(meetingId: number, maxParticipants: number) {
  const headers = userService.getAuthHeaders();
  const data = { meetingId, maxParticipants };
  return (await axiosInstance.post("/Meeting/UpdateMaxParticipants", data, { headers })).data;
}

// Hàm edit số lượng camera được bật
export async function updateConfigMeeting(meetingId: number, maxCam: number) {
  const headers = userService.getAuthHeaders();
  const data = {
    meetingId,
    maxCam
  };
  return (await axiosInstance.post("/Meeting/UpdateMaxCam", data, { headers })).data;
}

